// src/hooks/usePricing.js
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useData } from "./useData";

const PricingContext = createContext();

export function PricingProvider({ children }) {
  const { lines, quantity, selectedProduct, updateLine } = useData();
  const [totalPrice, setTotalPrice] = useState(0);

  const formatPrice = (price) => `$${Number(price).toFixed(2)}`;

  // SINGLE SOURCE OF TRUTH
  const calculateLinePrice = (line) => {
    const text = line.text?.trim();
    if (!text || !selectedProduct) return 0;

    const size = selectedProduct.sizes.find((s) => s.code === line.size);
    if (!size?.charPrice) return 0;

    let charPrice = size.charPrice;

    // Metallic colors +25%
    if (["gold_213", "silver_801"].includes(line.color)) charPrice *= 1.25;

    return Number((charPrice * text.length).toFixed(2));
  };

  const updatePricing = useCallback(() => {
    if (!lines.length || !selectedProduct) {
      setTotalPrice(0);
      return;
    }

    let subtotal = 0;

    lines.forEach((line) => {
      const text = line.text?.trim();
      if (!text) return;

      const linePrice = calculateLinePrice(line);
      subtotal += linePrice;

      // SAVE PRICE TO LINE — THIS IS THE KEY
      if (line.price !== linePrice) {
        updateLine(line.id, { price: linePrice });
      }
    });

    const total = Number((subtotal * quantity).toFixed(2));
    setTotalPrice(total);
  }, [lines, selectedProduct, quantity, updateLine]);

  // AUTO-UPDATE
  useEffect(() => {
    updatePricing();
  }, [updatePricing]);

  return (
    <PricingContext.Provider
      value={{ totalPrice, formatPrice, updatePricing, calculateLinePrice }}
    >
      {children}
    </PricingContext.Provider>
  );
}

export const usePricing = () => {
  const context = useContext(PricingContext);
  if (!context)
    throw new Error("usePricing must be used within PricingProvider");
  return context;
};
